import React, { useEffect, useState, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookOpen, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../context/AuthProvider";
import { useToast } from "../ui/Toast";
import { CampaignAssetService } from "../../services/campaignAssetService";
import { CampaignAsset, CharacterSummary } from "../../types/types";
import { LoadingState, EmptyState } from "../ui";
import KnowledgeTree from "./KnowledgeTree";
import KnowledgeDetailPanel from "./KnowledgeDetailPanel";
import { useOptionalWebSocketContext } from "../../contexts/WebSocketContext";
import { KnowledgeUpdatedData } from "../../types/websocket";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CharacterKnowledgeContentProps {
  campaignId: number;
  /** "full" = campaign knowledge tab; "compact" = session floating panel */
  mode: "full" | "compact";
  /** Campaign owner's username — used to show DM UI labels. */
  campaignCreatedBy?: string;
  /** Called when a reference chip is clicked for an unknown asset (no-op by default). */
  onUnknownAssetClick?: (_assetId: number) => void;
}

// ── localStorage key helpers ──────────────────────────────────────────────────

function localStorageKey(userId: string, campaignId: number) {
  return `rpg-knowledge-char-${userId}-${campaignId}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const CharacterKnowledgeContent: React.FC<CharacterKnowledgeContentProps> = ({
  campaignId,
  mode,
  campaignCreatedBy,
}) => {
  const auth = useAuth();
  const { showToast } = useToast();
  // Optional — only available when rendered inside a session (WebSocketProvider)
  const wsCtx = useOptionalWebSocketContext();
  const lastMessage = wsCtx?.lastMessage ?? null;

  const userId = auth.get("userId") ?? "";
  const username = auth.get("username") ?? "";
  const isDM = !!campaignCreatedBy && campaignCreatedBy === username;

  // ── State ────────────────────────────────────────────────────────────────────

  const [characters, setCharacters] = useState<CharacterSummary[]>([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState<number | null>(null);
  const [knowledgeTree, setKnowledgeTree] = useState<CampaignAsset[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [loadingChars, setLoadingChars] = useState(true);
  const [loadingTree, setLoadingTree] = useState(false);
  const [charSelectorOpen, setCharSelectorOpen] = useState(false);
  const [newlyRevealedId, setNewlyRevealedId] = useState<number | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Derived ──────────────────────────────────────────────────────────────────

  const selectedAsset = knowledgeTree.find((a) => a.id === selectedAssetId) ?? null;
  const accessibleIds = new Set(knowledgeTree.map((a) => a.id));

  // ── Load characters ────────────────────────────────────────────────────────
  // DMs get all campaign characters (with playerUsername); players get their own.

  const loadCharacters = useCallback(async () => {
    try {
      const chars = await CampaignAssetService.getMyCampaignCharacters(campaignId);
      setCharacters(chars);

      // Restore persisted selection
      const stored = localStorage.getItem(localStorageKey(userId, campaignId));
      const storedId = stored ? parseInt(stored, 10) : null;
      if (storedId && chars.some((c) => c.id === storedId)) {
        setSelectedCharacterId(storedId);
      } else if (chars.length > 0) {
        setSelectedCharacterId(chars[0].id);
      }
    } catch (err) {
      console.error("Failed to load campaign characters:", err);
    } finally {
      setLoadingChars(false);
    }
  }, [campaignId, userId]);

  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  // ── Load knowledge tree ────────────────────────────────────────────────────

  const loadKnowledgeTree = useCallback(
    async (charId: number, silent = false) => {
      if (!silent) setLoadingTree(true);
      try {
        const data = await CampaignAssetService.getCharacterKnowledgeTree(campaignId, charId);

        setKnowledgeTree((prev) => {
          // Detect newly added assets for the amber pulse highlight
          if (silent && prev.length > 0) {
            const prevIds = new Set(prev.map((a) => a.id));
            const newAsset = data.find((a) => !prevIds.has(a.id));
            if (newAsset) {
              setNewlyRevealedId(newAsset.id);
              showToast({
                type: "info",
                message: `The GM has revealed: ${newAsset.asset.name}`,
              });
              setTimeout(() => setNewlyRevealedId(null), 2500);
            }
            // If the currently selected asset was revoked, reset
            if (selectedAssetId && !data.find((a) => a.id === selectedAssetId)) {
              setSelectedAssetId(null);
            }
          }
          return data;
        });
      } catch (err) {
        console.error("Failed to load knowledge tree:", err);
      } finally {
        if (!silent) setLoadingTree(false);
      }
    },
    [campaignId, selectedAssetId, showToast]
  );

  useEffect(() => {
    if (selectedCharacterId !== null) {
      setSelectedAssetId(null);
      loadKnowledgeTree(selectedCharacterId);

      // Polling fallback for real-time knowledge updates (every 30 s)
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(() => {
        loadKnowledgeTree(selectedCharacterId, true);
      }, 30_000);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [selectedCharacterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Real-time refresh via WebSocket knowledge_updated event ───────────────
  useEffect(() => {
    if (!lastMessage || lastMessage.type !== "knowledge_updated") return;
    const data = lastMessage.data as KnowledgeUpdatedData;
    // Refresh if the event targets the currently viewed character
    if (selectedCharacterId !== null && data.characterId === selectedCharacterId) {
      loadKnowledgeTree(selectedCharacterId, true);
    }
  }, [lastMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Character selection ───────────────────────────────────────────────────

  const handleSelectCharacter = (charId: number) => {
    setSelectedCharacterId(charId);
    localStorage.setItem(localStorageKey(userId, campaignId), String(charId));
    setCharSelectorOpen(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loadingChars) {
    return (
      <LoadingState
        variant="spinner"
        message="Loading knowledge view…"
        size="md"
        fullHeight={false}
      />
    );
  }

  const selectedChar = characters.find((c) => c.id === selectedCharacterId);
  const isSingleChar = characters.length === 1;
  const compact = mode === "compact";

  // ── Character selector bar ─────────────────────────────────────────────────

  const CharacterSelector = () => {
    if (characters.length === 0) {
      return (
        <div
          className={`flex items-center gap-2 border-b border-zinc-800 ${compact ? "px-3 py-2" : "px-4 py-2.5"}`}
        >
          <span className="text-xs text-zinc-500">
            {isDM ? "No characters in this campaign yet." : "No characters in this campaign"}
          </span>
        </div>
      );
    }

    return (
      <div
        className={`flex items-center gap-3 border-b border-zinc-800 ${compact ? "px-3 py-2" : "px-4 py-2.5"} relative`}
      >
        <span className="text-xs text-zinc-500">{isDM ? "Character:" : "Viewing as:"}</span>
        {isSingleChar ? (
          /* Single char — non-interactive badge */
          <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-1 text-xs">
            <span className="w-4 h-4 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-400 text-[8px] font-bold shrink-0">
              {(selectedChar?.name ?? "?")[0].toUpperCase()}
            </span>
            <span className="text-zinc-200 font-medium">{selectedChar?.name}</span>
            {selectedChar?.class && <span className="text-zinc-500">· {selectedChar.class}</span>}
            {isDM && selectedChar?.playerUsername && (
              <span className="text-zinc-500 ml-1">
                · <span className="text-zinc-400">{selectedChar.playerUsername}</span>
              </span>
            )}
          </div>
        ) : (
          /* Multi-char dropdown */
          <div className="relative">
            <button
              className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-1 text-xs cursor-pointer hover:border-zinc-600 transition-colors"
              onClick={() => setCharSelectorOpen((prev) => !prev)}
            >
              <span className="w-4 h-4 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-400 text-[8px] font-bold shrink-0">
                {(selectedChar?.name ?? "?")[0].toUpperCase()}
              </span>
              <span className="text-zinc-200 font-medium">{selectedChar?.name}</span>
              {selectedChar?.class && <span className="text-zinc-500">· {selectedChar.class}</span>}
              {isDM && selectedChar?.playerUsername && (
                <span className="text-zinc-500 ml-1">
                  · <span className="text-zinc-400">{selectedChar.playerUsername}</span>
                </span>
              )}
              <FontAwesomeIcon icon={faChevronDown} className="text-zinc-500 text-[9px] ml-1" />
            </button>
            {charSelectorOpen && (
              <div className="absolute top-full left-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl z-50 min-w-[200px]">
                {characters.map((c) => (
                  <button
                    key={c.id}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-zinc-800 transition-colors text-left ${c.id === selectedCharacterId ? "text-amber-400" : "text-zinc-300"}`}
                    onClick={() => handleSelectCharacter(c.id)}
                  >
                    <span className="w-4 h-4 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-400 text-[8px] font-bold shrink-0">
                      {c.name[0].toUpperCase()}
                    </span>
                    <span className="flex-1 min-w-0 truncate">
                      {c.name}
                      {c.class && <span className="text-zinc-500"> · {c.class}</span>}
                    </span>
                    {isDM && c.playerUsername && (
                      <span className="text-zinc-600 text-[10px] shrink-0">{c.playerUsername}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!compact && selectedChar && (
          <div className="flex items-center gap-3 ml-2">
            <span className="text-[11px] text-zinc-500">
              <span className="text-zinc-300 font-medium">{knowledgeTree.length}</span> assets known
            </span>
          </div>
        )}

        <div className="ml-auto flex items-center gap-1.5 text-xs text-zinc-500">
          <FontAwesomeIcon icon={faBookOpen} className="text-[10px]" />
          <span>read only</span>
        </div>
      </div>
    );
  };

  // Show "no character" state after loading
  if (characters.length === 0) {
    return (
      <div className="flex flex-col h-full overflow-hidden bg-zinc-900">
        <CharacterSelector />
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            title="No Characters"
            description={
              isDM
                ? "No characters have been added to this campaign yet."
                : "You don't have any characters in this campaign yet."
            }
            icon="info"
            size="md"
          />
        </div>
      </div>
    );
  }

  if (selectedCharacterId === null) {
    return (
      <div className="flex flex-col h-full overflow-hidden bg-zinc-900">
        <CharacterSelector />
      </div>
    );
  }

  // ── Full-mode layout (side-by-side) ────────────────────────────────────────

  if (mode === "full") {
    return (
      <div
        className="flex flex-col overflow-hidden bg-zinc-950"
        style={{ height: "calc(100vh - 290px)", minHeight: "520px" }}
      >
        <CharacterSelector />
        <div className="flex flex-1 overflow-hidden">
          {/* Left — Tree */}
          <aside className="w-80 min-w-[200px] bg-zinc-900 border-r border-zinc-800 flex flex-col overflow-hidden shrink-0">
            {loadingTree ? (
              <div className="flex-1 flex items-center justify-center">
                <LoadingState variant="spinner" message="" size="sm" fullHeight={false} />
              </div>
            ) : (
              <KnowledgeTree
                assets={knowledgeTree}
                selectedId={selectedAssetId}
                newlyRevealedId={newlyRevealedId}
                onSelect={setSelectedAssetId}
              />
            )}
          </aside>

          <div className="w-1 bg-zinc-800 hover:bg-amber-400/40 cursor-col-resize shrink-0 transition-colors" />

          {/* Right — Detail */}
          <main className="flex-1 overflow-y-auto bg-zinc-950 codex-scroll">
            {loadingTree ? (
              <div className="flex items-center justify-center h-full">
                <LoadingState variant="spinner" message="Loading…" size="sm" fullHeight={false} />
              </div>
            ) : !selectedAsset ? (
              <div className="h-full flex items-center justify-center">
                <EmptyState
                  title="Select an asset"
                  description="Choose an asset from the tree on the left to view its details."
                  icon="folder"
                  size="md"
                />
              </div>
            ) : (
              <KnowledgeDetailPanel
                key={selectedAsset.id}
                asset={selectedAsset}
                allAssets={knowledgeTree}
                accessibleIds={accessibleIds}
                onSelectAsset={setSelectedAssetId}
              />
            )}
          </main>
        </div>
      </div>
    );
  }

  // ── Compact mode layout (session panel — stacked vertically) ───────────────

  return (
    <div className="flex flex-col h-full overflow-hidden bg-zinc-900">
      <CharacterSelector />

      {loadingTree ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingState variant="spinner" message="" size="sm" fullHeight={false} />
        </div>
      ) : (
        <>
          <div
            className="border-b border-zinc-800 overflow-hidden"
            style={{
              flex: selectedAsset ? "0 0 auto" : "1",
              minHeight: "120px",
              maxHeight: selectedAsset ? "45%" : undefined,
            }}
          >
            <KnowledgeTree
              assets={knowledgeTree}
              selectedId={selectedAssetId}
              newlyRevealedId={newlyRevealedId}
              onSelect={setSelectedAssetId}
              compact
            />
          </div>

          {/* Detail sub-panel */}
          {selectedAsset && (
            <div className="flex-1 overflow-y-auto codex-scroll">
              <KnowledgeDetailPanel
                key={selectedAsset.id}
                asset={selectedAsset}
                allAssets={knowledgeTree}
                accessibleIds={accessibleIds}
                onSelectAsset={setSelectedAssetId}
                compact
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CharacterKnowledgeContent;
